import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { Accordion, Button, Card, Modal, Spinner, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
// import axios from 'axios'

import { GET_BOTMAKER_WHATSAPP_TEMPLATES } from '../../gql/queries/botmaker'
import { SYNC_BOTMAKER_TEMPLATES, TOGGLE_BOTMAKER_TEMPLATE_ACTIVE, ARCHIVE_BOTMAKER_TEMPLATE, DELETE_BOTMAKER_WHATSAPP_TEMPLATE } from '../../gql/mutations/botmaker'
import { WhatsAppPreview } from '../templates/components/tiptap/WhatsAppPreview'
import { CreateBotmakerTemplateModal } from './CreateBotmakerTemplateModal'
import { TemplateConfigurationModal } from './TemplateConfigurationModal'

const MessageLogsModal = ({ show, onHide }: { show: boolean; onHide: () => void }) => {
  const { data, loading, refetch } = useQuery(gql`
        query GetBotmakerMessageLogs {
            botmakerMessageLogs(limit: 50) {
            id
            createdAt
            toPhone
            templateName
            module
            botmakerId
            user
            payload
            }
        }
    `, {
    fetchPolicy: 'network-only',
    skip: !show
  })

  const logs = data?.botmakerMessageLogs || []

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Message Logs (Last 50)</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-primary" size="sm" onClick={() => refetch()} disabled={loading}>
            <i className="bi bi-arrow-repeat me-1" /> Refresh
          </Button>
        </div>
        {loading && logs.length === 0 ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-5 text-muted">No logs found.</div>
        ) : (
          <Table bordered hover size="sm" className="small">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>To</th>
                <th>Template</th>
                <th>Module</th>
                <th>User</th>
                <th>Provider ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log: any) => (
                <tr key={log.id}>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>{log.toPhone}</td>
                  <td>{log.templateName}</td>
                  <td>{log.module}</td>
                  <td>{log.user}</td>
                  <td>
                    <code>{log.botmakerId}</code>
                  </td>
                  <td>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => {
                        // Simple alert for now, could be a nested modal
                        alert(JSON.stringify(log.payload, null, 2))
                      }}
                    >
                      Payload
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  )
}

type TemplateState = 'APPROVED' | 'REJECTED' | 'PENDING' | string

type WhatsAppTemplate = {
  id?: string
  name?: string
  locale?: string
  category?: string
  status?: TemplateState
  state?: TemplateState
  createdAt?: string
  updatedAt?: string
  botName?: string
  phoneLinesNumbers?: string[]
  optInImage?: string
  requesterEmail?: string
  body?: { text?: string }
  footer?: { text?: string }
  allowedModules?: string[]
  variableMapping?: Record<string, string>
  isActive?: boolean
  isArchived?: boolean
}


export const WhatsAppTemplatesPage = () => {
  const navigate = useNavigate()
  const [filterState, setFilterState] = useState<string>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(null)
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [configTemplate, setConfigTemplate] = useState<WhatsAppTemplate | null>(null)
  const [syncTemplates, { loading: syncing }] = useMutation(SYNC_BOTMAKER_TEMPLATES)
  const [toggleTemplateActive] = useMutation(TOGGLE_BOTMAKER_TEMPLATE_ACTIVE)
  const [archiveTemplate] = useMutation(ARCHIVE_BOTMAKER_TEMPLATE)
  const [deleteTemplate, { loading: deleting }] = useMutation(DELETE_BOTMAKER_WHATSAPP_TEMPLATE)

  const { data, loading, refetch } = useQuery(GET_BOTMAKER_WHATSAPP_TEMPLATES, {
    variables: { state: filterState || null },
    fetchPolicy: 'cache-and-network', // Ensure we always verify with server
    onCompleted: (d) => {
      console.log('Templates data updated:', d?.botmakerWhatsappTemplates?.length, 'items')
    },
    notifyOnNetworkStatusChange: true,
    onError: (err) => toast.error(err.message || 'Error loading templates'),
  })

  const templatesData = useMemo(() => data?.botmakerWhatsappTemplates || [], [data])

  useEffect(() => {
    refetch({ state: filterState || null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState])

  const handleSync = async () => {
    try {
      const { data } = await syncTemplates()
      if (data?.syncBotmakerTemplates?.success) {
        toast.success(`Synced ${data.syncBotmakerTemplates.count} templates`)
        refetch()
      } else {
        toast.error(data?.syncBotmakerTemplates?.message || 'Sync failed')
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || 'Sync failed')
    }
  }

  const handleToggleActive = async (template: WhatsAppTemplate) => {
    const newStatus = !template.isActive
    const action = newStatus ? 'activate' : 'deactivate'

    if (!window.confirm(`Are you sure you want to ${action} the template "${template.name}"?`)) {
      return
    }

    try {
      const { data } = await toggleTemplateActive({
        variables: {
          botmakerId: template.id,
          isActive: newStatus
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.toggleBotmakerTemplateActive?.success) {
        toast.success(data.toggleBotmakerTemplateActive.message)
        // Force a refetch to ensure UI updates
        await refetch()
      } else {
        toast.error('Failed to toggle template status')
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || 'Failed to toggle template status')
    }
  }

  const handleArchive = async (template: WhatsAppTemplate) => {
    if (!window.confirm(`Are you sure you want to ARCHIVE the template "${template.name}"?\n\nArchived templates cannot be used anymore (usually because they were deleted from Meta).`)) {
      return
    }

    try {
      const { data } = await archiveTemplate({
        variables: {
          botmakerId: template.id
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.archiveBotmakerTemplate?.success) {
        toast.success(data.archiveBotmakerTemplate.message)
        // Force a refetch to ensure UI updates
        await refetch()
      } else {
        toast.error('Failed to archive template')
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || 'Failed to archive template')
    }
  }

  const handleDelete = async (template: WhatsAppTemplate) => {
    if (!window.confirm(`¡CUIDADO! ¿Estás seguro de que quieres BORRAR PERMANENTEMENTE la plantilla "${template.name}" de la base de datos local?\n\nEsta acción no se puede deshacer.`)) {
      return
    }

    try {
      const { data } = await deleteTemplate({
        variables: {
          botmakerId: template.id
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.deleteBotmakerWhatsappTemplate?.success) {
        toast.success(data.deleteBotmakerWhatsappTemplate.message)
        await refetch()
      } else {
        toast.error('Error al borrar la plantilla')
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || 'Error al borrar la plantilla')
    }
  }

  return (
    <div className='container-fluid'>
      <div className='d-flex align-items-center justify-content-between mb-4'>
        <div>
          <h3>WhatsApp Templates (Botmaker)</h3>
          <p className='text-muted mb-0'>
            List, filter and create WhatsApp templates using your Botmaker account.
          </p>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' onClick={() => navigate(-1)}>
            <i className='bi bi-arrow-left-short me-1' />
            Back
          </Button>
          <Button
            variant='outline-primary'
            onClick={() => refetch({ state: filterState || null })}
            disabled={loading}
          >
            <i className='bi bi-arrow-repeat me-1' />
            Refresh
          </Button>
          <Button
            variant='outline-secondary'
            onClick={() => setShowLogsModal(true)}
          >
            <i className='bi bi-list-ul me-1' />
            Logs
          </Button>
          <Button
            variant='info'
            onClick={handleSync}
            disabled={syncing || loading}
            className='text-white'
          >
            {syncing ? <Spinner animation='border' size='sm' className='me-1' /> : <i className='bi bi-cloud-arrow-down-fill me-1' />}
            Sync
          </Button>
          <Button variant='primary' onClick={() => setShowCreateModal(true)}>
            <i className='bi bi-plus-lg me-1' />
            Create Template
          </Button>
        </div>
      </div>

      <Card>
        <Card.Header>Templates</Card.Header>
        <Card.Body>
          {loading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Meta Status</th>
                  <th>Habilitada</th>
                  <th>Locale</th>
                  <th>Category</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templatesData.length === 0 && (
                  <tr>
                    <td colSpan={8} className='text-center text-muted py-3'>
                      No templates found.
                    </td>
                  </tr>
                )}
                {templatesData.map((tpl) => {
                  const isArchived = tpl.isArchived === true
                  const isActive = tpl.isActive !== false && !isArchived

                  return (
                    <tr
                      key={tpl.id || tpl.name}
                      style={{
                        opacity: isArchived ? 0.4 : (isActive ? 1 : 0.65),
                        backgroundColor: isArchived ? '#f5f5f5' : (isActive ? 'transparent' : '#f8f9fa')
                      }}
                    >
                      <td>
                        {tpl.name || '—'}
                        {isArchived && (
                          <span className='badge bg-dark ms-2' style={{ fontSize: '0.7rem' }}>
                            ARCHIVED
                          </span>
                        )}
                      </td>
                      <td>
                        <span className='badge bg-light text-dark'>
                          {tpl.status || tpl.state || 'UNKNOWN'}
                        </span>
                      </td>
                      <td>
                        {isArchived ? (
                          <span className='badge bg-dark'>Archivada</span>
                        ) : isActive ? (
                          <span className='badge bg-success'>Sí</span>
                        ) : (
                          <span className='badge bg-secondary'>No</span>
                        )}
                      </td>
                      <td>{tpl.locale || '—'}</td>
                      <td>{tpl.category || '—'}</td>
                      <td>
                        {tpl.updatedAt
                          ? new Date(tpl.updatedAt).toLocaleString()
                          : tpl.createdAt
                            ? new Date(tpl.createdAt).toLocaleString()
                            : '—'}
                      </td>
                      <td className='text-end'>
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          className='me-2'
                          onClick={() => {
                            const parsed = { ...tpl }
                            try {
                              if (typeof tpl.body === 'string') {
                                parsed.body = JSON.parse(tpl.body)
                              }
                              if (typeof tpl.footer === 'string') {
                                parsed.footer = JSON.parse(tpl.footer)
                              }
                              if (typeof tpl.variableMapping === 'string') {
                                parsed.variableMapping = JSON.parse(tpl.variableMapping)
                              }
                            } catch (e) {
                              console.error('Error parsing template content', e)
                            }
                            setSelected(parsed)
                          }}
                        >
                          <i className='bi bi-eye' />
                        </Button>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          onClick={() => {
                            let parsedMapping = {}
                            try {
                              if (typeof tpl.variableMapping === 'string') {
                                parsedMapping = JSON.parse(tpl.variableMapping)
                              } else {
                                parsedMapping = tpl.variableMapping || {}
                              }
                            } catch (e) { }

                            setConfigTemplate({
                              ...tpl,
                              variableMapping: parsedMapping
                            })
                            setShowConfigModal(true)
                          }}
                        >
                          <i className='bi bi-gear' />
                        </Button>
                        <Button
                          variant={isActive ? 'outline-danger' : 'outline-success'}
                          size='sm'
                          className='ms-2'
                          onClick={() => handleToggleActive(tpl)}
                          title={isActive ? 'Deactivate template' : 'Activate template'}
                          disabled={isArchived}
                        >
                          <i className={isActive ? 'bi bi-toggle-on' : 'bi bi-toggle-off'} />
                        </Button>
                        {!isArchived && (
                          <Button
                            variant='outline-warning'
                            size='sm'
                            className='ms-2'
                            onClick={() => handleArchive(tpl)}
                            title='Archive template (for templates deleted from Meta)'
                          >
                            <i className='bi bi-archive' />
                          </Button>
                        )}
                        <Button
                          variant='outline-danger'
                          size='sm'
                          className='ms-2'
                          onClick={() => handleDelete(tpl)}
                          title='Delete template from database'
                          disabled={deleting}
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>Template details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex justify-content-between'>
                <div>
                  <div className='fw-bold'>{selected.name || '—'}</div>
                  <div className='text-muted'>{selected.category || '—'}</div>
                </div>
                <div className='text-end'>
                  <div className='badge bg-light text-dark'>
                    {selected.status || selected.state || 'UNKNOWN'}
                  </div>
                  <div className='small text-muted'>
                    {selected.updatedAt
                      ? new Date(selected.updatedAt).toLocaleString()
                      : selected.createdAt
                        ? new Date(selected.createdAt).toLocaleString()
                        : ''}
                  </div>
                </div>
              </div>
              <div className='small text-muted'>Locale: {selected.locale || '—'}</div>
              {selected.botName && <div className='small'>Bot: {selected.botName}</div>}
              {selected.phoneLinesNumbers && selected.phoneLinesNumbers.length > 0 && (
                <div className='small'>Lines: {selected.phoneLinesNumbers.join(', ')}</div>
              )}
              {selected.requesterEmail && (
                <div className='small'>Requester: {selected.requesterEmail}</div>
              )}
              {selected.optInImage && (
                <div className='small'>
                  Opt-in image:{' '}
                  <a href={selected.optInImage} target='_blank' rel='noreferrer'>
                    View
                  </a>
                </div>
              )}

              <Accordion defaultActiveKey='0' className='mb-3'>
                <Accordion.Item eventKey='0'>
                  <Accordion.Header>Preview WhatsApp</Accordion.Header>
                  <Accordion.Body>
                    {selected.body?.text ? (
                      <WhatsAppPreview
                        message={selected.body?.text || ''}
                        header={undefined}
                        footer={selected.footer?.text}
                      />
                    ) : (
                      <div className='text-muted'>Sin cuerpo de mensaje para previsualizar.</div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey='1'>
                  <Accordion.Header>Contenido (Texto & Footer)</Accordion.Header>
                  <Accordion.Body>
                    <h6 className='fw-bold mb-2'>Body Text</h6>
                    <pre className='mb-3 bg-light p-2 rounded' style={{ whiteSpace: 'pre-wrap' }}>
                      {selected.body?.text || '—'}
                    </pre>

                    {selected.footer?.text && (
                      <>
                        <h6 className='fw-bold mb-2'>Footer</h6>
                        <pre
                          className='mb-0 bg-light p-2 rounded'
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {selected.footer.text}
                        </pre>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CreateBotmakerTemplateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => refetch({ state: filterState || null })}
      />

      <MessageLogsModal show={showLogsModal} onHide={() => setShowLogsModal(false)} />

      {configTemplate && (
        <TemplateConfigurationModal
          show={showConfigModal}
          onHide={() => {
            setShowConfigModal(false)
            setConfigTemplate(null)
          }}
          template={configTemplate}
          onSuccess={() => refetch()}
        />
      )}

      {loading && (
        <div
          className='d-flex flex-center'
          style={{
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
            color: 'white',
            opacity: 0.35,
            position: 'fixed',
            zIndex: 10000000,
          }}
        >
          <div>
            <Spinner animation='border' style={{ height: '3em', width: '3em' }} />
          </div>
        </div>
      )}
    </div>
  )
}

export default WhatsAppTemplatesPage
